/**
 * SoleadLab 2.0 — Finding Entity API Client
 *
 * B3.3: Frontend service for finding entity CRUD with status workflow.
 *
 * Read:  fetchFindings (auto-paginated list), fetchSingleFinding
 * Write: createFinding, updateFinding (If-Match ETag concurrency)
 *
 * V4 Invariant #6: source_observation_ids MUST be a non-empty array.
 *
 * Status workflow (backend-enforced state machine):
 *   draft → confirmed | rejected
 *   confirmed → approved | rejected
 *   approved  (terminal)
 *   rejected  (terminal)
 *
 * Concurrency: PATCH requires If-Match: <version>.
 * Conflict: 409 VERSION_CONFLICT → caught by isConflictError().
 * Bad transition: 422 INVALID_STATUS_TRANSITION → caught by isStatusTransitionError().
 *
 * Follows the same per-service pattern as observationApi.ts.
 */

import { apiFetch, apiFetchWithEtag } from './apiFetch';
export { isConflictError } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────────

/** Raw row shape returned by the finding entity API */
export interface FindingEntityRow {
  id: string;
  site_id: string;
  session_id: string | null;
  finding_type: string;
  severity: string;
  confidence: number;
  title: string;
  description: string | null;
  source_observation_ids: string;      // JSON string: string[]
  source_evidence_ids: string | null;  // JSON string: string[] | null
  asset_id: string | null;
  rule_execution_id: string | null;
  metrics_json: string | null;
  impact_json: string | null;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Paginated list response */
interface FindingListResponse {
  items: FindingEntityRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/** Frontend finding shape (parsed JSON fields) */
export interface Finding {
  id: string;
  siteId: string;
  sessionId: string | null;
  findingType: string;
  severity: string;
  confidence: number;
  title: string;
  description: string | null;
  sourceObservationIds: string[];
  sourceEvidenceIds: string[];
  assetId: string | null;
  ruleExecutionId: string | null;
  metrics: Record<string, unknown> | null;
  impact: Record<string, unknown> | null;
  status: FindingStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

/** Status workflow values */
export type FindingStatus = 'draft' | 'confirmed' | 'approved' | 'rejected';

/** Filters for list endpoint */
export interface FindingFilters {
  status?: FindingStatus;
  findingType?: string;
  severity?: string;
  sessionId?: string;
  assetId?: string;
}

// ─── Fetch Utilities (thin adapters around centralized apiFetch) ──────────────

async function apiFetchGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

async function apiFetchMutate<T>(
  path: string,
  method: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<{ data: T; etag: number }> {
  return apiFetchWithEtag<T>(path, {
    method,
    headers: extraHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

// ─── Transform ──────────────────────────────────────────────────────────────────

/** Parse a JSON string into a string[] safely */
function parseIdArray(json: string | null): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

/** Parse a JSON string into a Record safely */
function parseRecord(json: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Entity row → frontend Finding shape */
function entityRowToFinding(row: FindingEntityRow): Finding {
  return {
    id: row.id,
    siteId: row.site_id,
    sessionId: row.session_id,
    findingType: row.finding_type,
    severity: row.severity,
    confidence: row.confidence,
    title: row.title,
    description: row.description,
    sourceObservationIds: parseIdArray(row.source_observation_ids),
    sourceEvidenceIds: parseIdArray(row.source_evidence_ids),
    assetId: row.asset_id,
    ruleExecutionId: row.rule_execution_id,
    metrics: parseRecord(row.metrics_json),
    impact: parseRecord(row.impact_json),
    status: row.status as FindingStatus,
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    version: row.version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
  };
}

/** Frontend Finding fields → POST body */
function findingToCreateBody(
  finding: Partial<Finding> & {
    findingType: string;
    severity: string;
    title: string;
    sourceObservationIds: string[];
  },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    finding_type: finding.findingType,
    severity: finding.severity,
    title: finding.title,
    source_observation_ids: finding.sourceObservationIds,
  };
  if (finding.id) body.id = finding.id;
  if (finding.sessionId) body.session_id = finding.sessionId;
  if (finding.description) body.description = finding.description;
  if (finding.sourceEvidenceIds?.length) body.source_evidence_ids = finding.sourceEvidenceIds;
  if (finding.assetId) body.asset_id = finding.assetId;
  if (finding.ruleExecutionId) body.rule_execution_id = finding.ruleExecutionId;
  if (finding.confidence !== undefined) body.confidence = finding.confidence;
  if (finding.status) body.status = finding.status;
  if (finding.metrics) body.metrics_json = JSON.stringify(finding.metrics);
  if (finding.impact) body.impact_json = JSON.stringify(finding.impact);
  return body;
}

// ─── Error Helpers ──────────────────────────────────────────────────────────────

/** 422 INVALID_STATUS_TRANSITION */
export function isStatusTransitionError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as Error & { status?: number; code?: string }).status === 422 &&
    (err as Error & { code?: string }).code === 'INVALID_STATUS_TRANSITION'
  );
}

// ─── Read API ───────────────────────────────────────────────────────────────────

/**
 * Fetch ALL findings for a site. Auto-paginates (200/page).
 * Supports optional filters.
 */
export async function fetchFindings(
  siteId: string,
  filters?: FindingFilters,
): Promise<{ items: Finding[]; total: number }> {
  const allItems: Finding[] = [];
  let page = 1;
  let total = 0;
  const limit = 200;

  while (true) {
    let url = `/api/v2/sites/${siteId}/findings?page=${page}&limit=${limit}`;
    if (filters?.status) url += `&status=${encodeURIComponent(filters.status)}`;
    if (filters?.findingType) url += `&finding_type=${encodeURIComponent(filters.findingType)}`;
    if (filters?.severity) url += `&severity=${encodeURIComponent(filters.severity)}`;
    if (filters?.sessionId) url += `&session_id=${encodeURIComponent(filters.sessionId)}`;
    if (filters?.assetId) url += `&asset_id=${encodeURIComponent(filters.assetId)}`;

    const result = await apiFetchGet<FindingListResponse>(url);

    if (page === 1) total = result.total;

    for (const row of result.items) {
      allItems.push(entityRowToFinding(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { items: allItems, total };
}

/**
 * Fetch a single finding by ID. Returns null on 404.
 */
export async function fetchSingleFinding(
  siteId: string,
  id: string,
): Promise<Finding | null> {
  try {
    const row = await apiFetchGet<FindingEntityRow>(
      `/api/v2/sites/${siteId}/findings/${id}`,
    );
    return entityRowToFinding(row);
  } catch {
    return null;
  }
}

// ─── Write API ──────────────────────────────────────────────────────────────────

/**
 * Create a finding.
 * V4 Invariant: sourceObservationIds MUST be non-empty.
 * Returns the created finding with version from server.
 */
export async function createFinding(
  siteId: string,
  finding: Partial<Finding> & {
    findingType: string;
    severity: string;
    title: string;
    sourceObservationIds: string[];
  },
): Promise<Finding> {
  const body = findingToCreateBody(finding);
  const { data: row, etag } = await apiFetchMutate<FindingEntityRow>(
    `/api/v2/sites/${siteId}/findings`,
    'POST',
    body,
  );
  const result = entityRowToFinding(row);
  result.version = etag || result.version;
  return result;
}

/**
 * Update a finding. Requires version for optimistic concurrency (If-Match).
 *
 * Status transitions are backend-enforced:
 *   draft → confirmed | rejected
 *   confirmed → approved | rejected
 *   approved, rejected → terminal
 *
 * Throws 409 on version conflict → handle via isConflictError().
 * Throws 422 on bad transition → handle via isStatusTransitionError().
 */
export async function updateFinding(
  siteId: string,
  id: string,
  updates: {
    status?: FindingStatus;
    severity?: string;
    confidence?: number;
    title?: string;
    description?: string;
    sourceObservationIds?: string[];
    sourceEvidenceIds?: string[];
    assetId?: string;
    metrics?: Record<string, unknown>;
    impact?: Record<string, unknown>;
  },
  version: number,
): Promise<Finding> {
  const patchBody: Record<string, unknown> = {};
  if (updates.status !== undefined) patchBody.status = updates.status;
  if (updates.severity !== undefined) patchBody.severity = updates.severity;
  if (updates.confidence !== undefined) patchBody.confidence = updates.confidence;
  if (updates.title !== undefined) patchBody.title = updates.title;
  if (updates.description !== undefined) patchBody.description = updates.description;
  if (updates.sourceObservationIds !== undefined) patchBody.source_observation_ids = updates.sourceObservationIds;
  if (updates.sourceEvidenceIds !== undefined) patchBody.source_evidence_ids = updates.sourceEvidenceIds;
  if (updates.assetId !== undefined) patchBody.asset_id = updates.assetId;
  if (updates.metrics !== undefined) patchBody.metrics_json = JSON.stringify(updates.metrics);
  if (updates.impact !== undefined) patchBody.impact_json = JSON.stringify(updates.impact);

  const { data: row, etag } = await apiFetchMutate<FindingEntityRow>(
    `/api/v2/sites/${siteId}/findings/${id}`,
    'PATCH',
    patchBody,
    { 'If-Match': String(version) },
  );
  const result = entityRowToFinding(row);
  result.version = etag || result.version;
  return result;
}
